import React, { useState, useEffect } from 'react';
import { X, Loader2, AlertCircle, CheckSquare, Square, RefreshCw } from 'lucide-react';
import { base44 } from '@/api/base44Client';

const STORAGE_KEY = 'square_sync_selected_categories';

const PRESETS = [
  {
    label: 'Physical Products Only',
    description: 'Excludes experiences and services',
    exclude: ['sim driving experiences'],
  },
  {
    label: 'Exclude Experiences',
    description: 'All categories except driving experiences',
    exclude: ['sim driving experiences'],
  },
];

async function fetchSquareCategories() {
  const token = null; // fetched server-side; we call a backend helper
  // We fetch via the existing sync endpoint in "dry-run" mode — actually
  // we expose a lightweight backend function to list categories.
  const res = await base44.functions.invoke('listSquareCategories', {});
  return res.data; // { categories: [{ id, name, item_count }] }
}

export default function SquareCategorySyncModal({ onClose, onConfirm, mode = 'sync' }) {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selected, setSelected] = useState(new Set());
  const [destinations, setDestinations] = useState({}); // lowercasedName -> 'shop'|'pos'|'both'
  const [warned, setWarned] = useState(false);

  const DEST_LABELS = { shop: 'Shop', pos: 'POS', both: 'Both' };
  // Merch defaults to Both — it sells at the venue POS and in the online store —
  // while drinks and snacks stay venue-only.
  function defaultDestFor(name) {
    const n = (name || '').toLowerCase();
    if (/beverage|snack/.test(n)) return 'pos';
    if (/merch/.test(n)) return 'both';
    return 'shop';
  }
  const destFor = (name) => destinations[name.toLowerCase()] || defaultDestFor(name);
  const setDest = (name, d) => setDestinations((prev) => ({ ...prev, [name.toLowerCase()]: d }));

  useEffect(() => {
    fetchSquareCategories()
      .then(({ categories: cats }) => {
        setCategories(cats || []);
        // Restore last selection
        try {
          const saved = JSON.parse(localStorage.getItem(STORAGE_KEY) || '[]');
          const savedSet = new Set(saved);
          // Only keep saved names that still exist
          const valid = new Set(cats.filter(c => savedSet.has(c.name.toLowerCase())).map(c => c.name.toLowerCase()));
          setSelected(valid.size > 0 ? valid : new Set());
          try {
            const savedDest = JSON.parse(localStorage.getItem('square_sync_destinations') || '{}');
            const validDest = {};
            Object.keys(savedDest).forEach((k) => {
              if (valid.has(k) && ['shop', 'pos', 'both'].includes(savedDest[k])) validDest[k] = savedDest[k];
            });
            setDestinations(validDest);
          } catch { /* ignore */ }
        } catch {
          // ignore
        }
      })
      .catch(e => setError(e.message || 'Failed to load Square categories'))
      .finally(() => setLoading(false));
  }, []);

  const toggle = (name) => {
    const key = name.toLowerCase();
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
        setDestinations((d) => (d[key] ? d : { ...d, [key]: defaultDestFor(name) }));
      }
      return next;
    });
  };

  const selectAll = () => {
    setSelected(new Set(categories.map(c => c.name.toLowerCase())));
    setDestinations((prev) => {
      const next = { ...prev };
      categories.forEach((c) => { const k = c.name.toLowerCase(); if (!next[k]) next[k] = defaultDestFor(c.name); });
      return next;
    });
  };
  const selectNone = () => setSelected(new Set());

  const applyPreset = (preset) => {
    const allNames = new Set(categories.map(c => c.name.toLowerCase()));
    preset.exclude.forEach(e => allNames.delete(e));
    setSelected(allNames);
    setDestinations((prev) => {
      const next = { ...prev };
      categories.forEach((c) => {
        const k = c.name.toLowerCase();
        if (allNames.has(k) && !next[k]) next[k] = defaultDestFor(c.name);
      });
      return next;
    });
  };

  const handleConfirm = () => {
    if (selected.size === 0) {
      setWarned(true);
      return;
    }
    // Persist selection + per-category destinations
    const destMap = {};
    selected.forEach((k) => { destMap[k] = destFor(k); });
    localStorage.setItem(STORAGE_KEY, JSON.stringify([...selected]));
    localStorage.setItem('square_sync_destinations', JSON.stringify(destMap));
    onConfirm({ categories: [...selected], destinations: destMap });
  };

  const title = mode === 'reimport' ? 'RE-IMPORT FROM SQUARE' : 'SYNC FROM SQUARE';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm">
      <div className="bg-[#111115] border border-white/10 w-full max-w-lg max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-white/5 shrink-0">
          <div>
            <p className="text-primary text-[10px] font-bold tracking-widest">{title}</p>
            <p className="text-white/40 text-xs mt-0.5">Select categories, then choose Shop / POS / Both for each</p>
          </div>
          <button onClick={onClose} className="text-white/40 hover:text-white transition-colors">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-6 py-4">
          {loading ? (
            <div className="flex items-center justify-center py-16">
              <Loader2 className="w-6 h-6 animate-spin text-primary" />
            </div>
          ) : error ? (
            <div className="flex items-start gap-2.5 px-4 py-3 bg-red-500/5 border border-red-500/20">
              <AlertCircle className="w-4 h-4 text-red-400 mt-0.5 shrink-0" />
              <p className="text-red-300/80 text-xs">{error}</p>
            </div>
          ) : (
            <>
              {/* Presets */}
              <div className="mb-4">
                <p className="text-white/30 text-[10px] font-bold tracking-widest mb-2">PRESETS</p>
                <div className="flex flex-wrap gap-2">
                  {PRESETS.map(p => (
                    <button
                      key={p.label}
                      onClick={() => applyPreset(p)}
                      className="text-xs font-bold text-white/50 hover:text-white border border-white/10 hover:border-white/30 px-3 py-1.5 transition-all"
                    >
                      {p.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Select all / none */}
              <div className="flex items-center gap-4 mb-3">
                <button onClick={selectAll} className="flex items-center gap-1.5 text-xs text-white/40 hover:text-white transition-colors">
                  <CheckSquare className="w-3.5 h-3.5" /> Select all
                </button>
                <button onClick={selectNone} className="flex items-center gap-1.5 text-xs text-white/40 hover:text-white transition-colors">
                  <Square className="w-3.5 h-3.5" /> Select none
                </button>
                <span className="ml-auto text-xs text-white/30">{selected.size} of {categories.length} selected</span>
              </div>

              {/* Category list */}
              <div className="space-y-1">
                {categories.map(cat => {
                  const key = cat.name.toLowerCase();
                  const checked = selected.has(key);
                  return (
                    <div
                      key={cat.id}
                      className={`w-full flex items-center gap-3 px-4 py-3 border transition-all ${
                        checked
                          ? 'bg-primary/5 border-primary/20'
                          : 'bg-[#0d0d0f] border-white/5 hover:border-white/15'
                      }`}
                    >
                      <button
                        onClick={() => toggle(cat.name)}
                        className="flex items-center gap-3 flex-1 min-w-0 text-left"
                      >
                        <div className={`w-4 h-4 border flex items-center justify-center shrink-0 transition-colors ${
                          checked ? 'bg-primary border-primary' : 'border-white/20'
                        }`}>
                          {checked && <svg className="w-2.5 h-2.5 text-white" viewBox="0 0 10 10" fill="none">
                            <path d="M2 5l2.5 2.5L8 3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                          </svg>}
                        </div>
                        <span className={`text-sm font-medium flex-1 truncate ${checked ? 'text-white' : 'text-white/60'}`}>
                          {cat.name}
                        </span>
                        <span className="text-xs text-white/25 font-mono shrink-0">
                          {cat.item_count} item{cat.item_count !== 1 ? 's' : ''}
                        </span>
                      </button>
                      {checked && (
                        <div className="flex items-center gap-1 shrink-0" onClick={(e) => e.stopPropagation()}>
                          {['shop', 'pos', 'both'].map((d) => {
                            const active = destFor(cat.name) === d;
                            return (
                              <button
                                key={d}
                                onClick={() => setDest(cat.name, d)}
                                className={`text-[10px] font-bold tracking-wider px-2 py-1 border transition-colors ${
                                  active
                                    ? 'bg-primary border-primary text-white'
                                    : 'border-white/15 text-white/45 hover:text-white hover:border-white/35'
                                }`}
                              >
                                {DEST_LABELS[d].toUpperCase()}
                              </button>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>

              {warned && selected.size === 0 && (
                <div className="flex items-center gap-2 mt-3 px-4 py-2.5 bg-yellow-500/5 border border-yellow-500/20">
                  <AlertCircle className="w-4 h-4 text-yellow-400 shrink-0" />
                  <p className="text-yellow-300/80 text-xs">Please select at least one category to proceed.</p>
                </div>
              )}
            </>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-white/5 flex gap-3 shrink-0">
          <button onClick={onClose}
            className="flex-1 border border-white/10 hover:border-white/25 text-white/50 hover:text-white text-xs font-bold tracking-wider py-3 transition-all">
            CANCEL
          </button>
          <button
            onClick={handleConfirm}
            disabled={loading || !!error}
            className="flex-1 bg-primary hover:bg-primary/90 disabled:opacity-40 text-white font-bold text-xs tracking-wider py-3 transition-colors flex items-center justify-center gap-2"
          >
            <RefreshCw className="w-4 h-4" />
            {mode === 'reimport' ? 'RE-IMPORT' : 'SYNC'} {selected.size > 0 ? `(${selected.size} CATEGOR${selected.size === 1 ? 'Y' : 'IES'})` : ''}
          </button>
        </div>
      </div>
    </div>
  );
}